// Lazy-loaded CPV 2008 catalog (main vocabulary, German labels).
//
// Source: official CPV nomenclature (Regulation (EC) No 213/2008), downloaded
// from ted.europa.eu; codes are stored without the check digit (matching how
// tender search profiles store them, e.g. "50700000" or the wildcard
// "9834113*"). The catalog is a separate webpack chunk so it only loads when
// the profile editor needs it.

export interface CpvEntry {
  code: string;
  label: string;
}

let catalogPromise: Promise<CpvEntry[]> | null = null;

export function loadCpvCatalog(): Promise<CpvEntry[]> {
  if (!catalogPromise) {
    catalogPromise = import(
      /* webpackChunkName: "cpv2008" */ "../data/cpv2008.de.json"
    ).then((module) => {
      const rows = (module.default ?? module) as [string, string][];
      return rows.map(([code, label]) => ({ code, label }));
    });
  }
  return catalogPromise;
}

// Resolve the label for a stored profile code. Wildcards ("9834113*") resolve
// to the first catalog entry the prefix matches.
export function cpvLabel(catalog: CpvEntry[], code: string): string | undefined {
  if (code.endsWith("*")) {
    const prefix = code.slice(0, -1);
    return catalog.find((e) => e.code.startsWith(prefix))?.label;
  }
  return catalog.find((e) => e.code === code)?.label;
}

// Rank matches for the picker: code prefix first, then label word starts,
// then label substring.
export function searchCpv(catalog: CpvEntry[], query: string, limit = 30): CpvEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  if (/^\d+$/.test(q)) {
    return catalog.filter((e) => e.code.startsWith(q)).slice(0, limit);
  }
  const wordStarts: CpvEntry[] = [];
  const includes: CpvEntry[] = [];
  for (const entry of catalog) {
    const label = entry.label.toLowerCase();
    const index = label.indexOf(q);
    if (index < 0) continue;
    if (index === 0 || /[\s(/-]/.test(label[index - 1])) wordStarts.push(entry);
    else includes.push(entry);
    if (wordStarts.length >= limit) break;
  }
  return [...wordStarts, ...includes].slice(0, limit);
}
